/**
 * V70 타자왕 — 타입 정의
 *
 * Phase 2 FE + Phase 3 BE 공유 가능한 shape.
 * 서버 interface 는 serverApi.ts 에 분리.
 */

export type GameStatus = 'idle' | 'countdown' | 'playing' | 'ended';

export interface AcidRainSettings {
  /** 게임 ON/OFF */
  enabled: boolean;
  /** 게임 길이 (초) — 사용자 지시: 120s 기본 */
  durationSec: number;
  /** 생명력 (초기값) — 사용자 지시: 5 */
  initialLives: number;
  /** 일일 코인 상한 — 사용자 지시: 5 */
  dailyCoinLimit: number;
  /** 서버 KST 자정 초기화 (true 고정) */
  kstMidnightReset: boolean;
  /** 난이도 증가 주기 (초) */
  difficultyRampSec: number;
  /** 난이도 ramp 배율 (낙하 속도) */
  fallSpeedMultiplier: number;
  /** 난이도 ramp 배율 (등장 간격 /x) */
  spawnIntervalDivisor: number;
  /**
   * V70 오픈 기간 설정 (하위 호환: null 이면 상시 오픈).
   * KST 서버 timestamp(ms). Phase 2 mock 은 localStorage, Phase 3 는 Firestore.
   */
  openStartAt?: number | null;
  openEndAt?: number | null;
}

export const DEFAULT_SETTINGS: AcidRainSettings = {
  enabled: false,
  durationSec: 120,
  initialLives: 5,
  dailyCoinLimit: 5,
  kstMidnightReset: true,
  difficultyRampSec: 10,
  fallSpeedMultiplier: 1.1,
  spawnIntervalDivisor: 1.1,
  openStartAt: null,
  openEndAt: null,
};

export interface FallingWord {
  /** 결정론적 큐 index */
  id: number;
  /** 단어 텍스트 */
  text: string;
  /** 화면 x 비율 (0~1) */
  xRatio: number;
  /** 생성 시각 (게임 ms) */
  spawnMs: number;
  /** 낙하 속도 (px/sec) */
  speed: number;
  /** 기본 점수 */
  baseScore: number;
}

export type GameEventType =
  | 'start'
  | 'word_spawn'
  | 'word_cleared'
  | 'word_missed'
  | 'combo_broken'
  | 'life_lost'
  | 'difficulty_ramp'
  | 'end';

export interface GameEvent {
  type: GameEventType;
  tMs: number;
  payload?: Record<string, unknown>;
}

export interface GameSnapshot {
  status: GameStatus;
  score: number;
  combo: number;
  maxCombo: number;
  lives: number;
  remainingSec: number;
  elapsedMs: number;
  cleared: number;
  missed: number;
  fallingWords: FallingWord[];
  typingBuffer: string;
  nextSpawnMs: number;
  difficultyLevel: number;
}

export interface GameResult {
  sessionId: string;
  startedAt: number;
  endedAt: number;
  elapsedMs: number;
  clientReportedScore: number;
  cleared: number;
  missed: number;
  maxCombo: number;
  reason: 'time_up' | 'no_lives' | 'user_quit';
  /**
   * 단어 사전 내용 버전 (wordDictionary.ts `WORD_DICTIONARY_VERSION`).
   * Phase 3 서버가 점수를 재계산할 때 같은 단어 큐를 만들 수 있는지 판정한다.
   */
  dictionaryVersion?: number;
  /**
   * 단어 생성 알고리즘 버전 (engine.ts `SPAWN_ALGORITHM_VERSION`).
   * seed 가 같아도 이 값이 다르면 단어 큐가 재현되지 않는다.
   */
  spawnAlgorithmVersion?: number;
}

export interface LeaderboardRow {
  rank: number;
  uid: string;
  displayName: string;
  companyCode?: 'KR' | 'CN';
  bestScore: number;
  recordedAt: number;
  /**
   * V70 Phase 2: 저장 시점의 소속 조직 ID (`UserProfile.orgUnitId`).
   * UI 는 조회 시점에 `useOrgUnitsPublic` 캐시로 조직명을 매핑한다.
   * - 조직 변경 즉시 반영은 아니며, 다음 플레이에서 새 orgUnitId 로 저장됨
   * - 조회 시 매핑되는 OrgUnit 이 없으면 "미지정" 등 대체 표시 (UI 결정)
   */
  orgUnitId?: string;
}

export type CoinPolicyMode = 'unlimited' | 'limited';

/**
 * 플레이 코인 정책 — system_admin 전용 설정
 *
 * - unlimited: 코인 차감 없음, 무제한 플레이 (잔여량 체크 skip)
 * - limited:   일일 플레이 가능 횟수 제한 (dailyLimit 사용)
 *
 * KST 자정 (00:00) 기준으로 당일 사용량 리셋.
 * 설정 변경은 신규 세션부터 적용, 진행 중 세션에는 소급 적용 없음.
 */
export interface CoinPolicy {
  mode: CoinPolicyMode;
  /** limited 일 때만 유효. 1~100 (기본 5). unlimited 일 때는 참조하지 않음 */
  dailyLimit: number;
}

export const DEFAULT_COIN_POLICY: CoinPolicy = {
  mode: 'limited',
  dailyLimit: 5,
};

export interface DailyCoinStatus {
  /** YYYY-MM-DD KST */
  ymd: string;
  /** 현재 정책 모드 (unlimited 일 때 used/limit/remaining 은 참고용) */
  mode: CoinPolicyMode;
  used: number;
  /** limited 일 때만 유효 상한. unlimited 일 때는 null */
  limit: number | null;
  /** limited 일 때: 남은 횟수. unlimited 일 때는 null */
  remaining: number | null;
}

export interface CoinPolicyChange {
  id: string;
  changedAt: number;
  changedByName: string | null;
  previous: CoinPolicy;
  next: CoinPolicy;
}
